import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Claims } from '@plantefeve/common/constants/enums/claims.enum';
import { Roles } from '@plantefeve/common/constants/enums/roles.enum';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NavigationsService {
  private readonly history: string[] = [];

  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly authService = inject(AuthService);

  public back(): void {
    this.history.pop();
    if (this.history.length > 0) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  public getNavigation(): Observable<MenuItem[]> {
    return this.isAdmin().pipe(
      map((isAdmin: boolean) => {
        const menu: MenuItem[] = [];

        menu.push({
          label: 'Artikels',
          icon: PrimeIcons.SUN,
          items: [
            {
              label: 'Overzicht',
              icon: PrimeIcons.SUN,
              routerLink: 'articles',
            },
            {
              label: 'Stock',
              icon: PrimeIcons.WAREHOUSE,
              routerLink: 'stock',
            },
          ],
        });

        menu.push({
          label: 'Relaties',
          icon: PrimeIcons.ID_CARD,
          items: [
            {
              label: 'Overzicht',
              icon: PrimeIcons.ID_CARD,
              routerLink: 'relations',
            },
          ],
        });

        menu.push({
          label: 'Contracten',
          icon: PrimeIcons.FILE,
          items: [
            {
              label: 'Aankoopcontracten',
              icon: PrimeIcons.CART_PLUS,
              routerLink: 'purchase-contracts',
            },
            {
              label: 'Verkoopcontracten',
              icon: PrimeIcons.SHOPPING_BAG,
              routerLink: 'sales-contracts',
            },
          ],
        });

        menu.push({
          label: 'Leveringen',
          icon: PrimeIcons.FILE,
          items: [
            {
              label: 'Overzicht',
              icon: PrimeIcons.SUN,
              routerLink: 'deliveries',
            },
          ],
        });

        menu.push({
          label: 'Ritten',
          icon: PrimeIcons.FILE,
          items: [
            {
              label: 'Afroepen',
              icon: PrimeIcons.RECEIPT,
              routerLink: 'orders',
            },
            {
              label: 'Rittenplanning',
              icon: PrimeIcons.CALENDAR,
              routerLink: 'plannings/graan',
            },
            {
              label: 'Graanplanning',
              icon: PrimeIcons.CALENDAR_CLOCK,
              routerLink: 'plannings/overview',
            },
            {
              label: 'Verlofplanning',
              icon: PrimeIcons.CALENDAR,
              routerLink: 'plannings/verlof',
            },
            {
              label: 'TAS-uitwisseling',
              icon: PrimeIcons.ARROW_RIGHT_ARROW_LEFT,
              routerLink: 'plannings/tas-uitwisseling',
            },
            {
              label: 'Te controleren TAS-dossiers',
              icon: PrimeIcons.INBOX,
              routerLink: 'plannings/tas-dossiers',
            },
          ],
        });
        if (!isAdmin) {
          return menu;
        }
        menu.push({
          label: 'Facturen',
          icon: PrimeIcons.FILE,
          items: [
            {
              label: 'Verkoopfacturen',
              icon: PrimeIcons.FILE_PDF,
              routerLink: 'sales-invoices',
            },
            {
              label: 'Aankoopfacturen',
              icon: PrimeIcons.RECEIPT,
              routerLink: 'purchase-invoices',
            },
          ],
        });

        menu.push({
          label: 'Rapportering',
          items: [
            {
              label: 'Toekomstige marges',
              icon: PrimeIcons.CHART_LINE,
              routerLink: 'reporting/future-margins',
            },
            {
              label: 'Historische marges',
              icon: PrimeIcons.TABLE,
              routerLink: 'reporting/historical-margins',
            },
            {
              label: 'Lopende contracten',
              icon: PrimeIcons.CHART_BAR,
              routerLink: 'reporting/contract-reports',
            },
          ],
        });

        menu.push({
          label: 'Instellingen',
          items: [
            {
              label: 'Parameters',
              routerLink: 'parameters',
              icon: PrimeIcons.COG,
            },
            {
              label: 'Majorations',
              routerLink: 'majorations',
              icon: PrimeIcons.MONEY_BILL,
            },
            {
              label: 'Trucks & Chauffeurs',
              routerLink: 'transportations',
              icon: PrimeIcons.TRUCK,
            },
            {
              label: 'Gebruikers',
              routerLink: 'users/backoffice',
              icon: PrimeIcons.USERS,
            },
          ],
        });
        return menu;
      })
    );
  }

  public isAdmin(): Observable<boolean> {
    return this.authService.user$.pipe(
      map((user) => {
        if (user?.[Claims.RoleIdentityClaim]) {
          return user[Claims.RoleIdentityClaim].some((role: string) => role === Roles.Administrator);
        }
        return false;
      })
    );
  }
}
